/**
 * Week · by line.
 *
 * Rows are the plant's work centers, columns Monday..Sunday. Each cell holds
 * the day's steps on that lane: a header (title, x/y heads; the window sits in
 * its tooltip) and, per crew group (phases sharing operation + role, see
 * `groupCrewLines`), the crew as name chips under one shift label per shift,
 * with a dashed "+ Assign" slot per open crew line. Chips drag to a group in
 * the SAME day column (whole step by default); a drop on another day is
 * refused as "different day". Day columns never go under MIN_DAY_WIDTH; the
 * lanes scroll sideways when the viewport is genuinely too narrow. A worker
 * list on the left edge drags names onto a lane's row header to pin them to
 * that line for the week.
 */
import { format } from "date-fns";
import React, { useCallback, useMemo } from "react";

import { ShiftInstance } from "../../../../../services/shifts-api";
import { hhmm } from "../../../utils/time";
import { DraftPin, ManualMoves } from "../controller/use-manual-moves";
import { bestPhaseTarget, dayPhaseDropState, DropState } from "../utils/drop-state"; // eslint-disable-line sort-imports
import { groupCrewLines, LaneCard, LaneCrewGroup, LaneSlot, LaneStep } from "../utils/lane-rows"; // eslint-disable-line sort-imports
import { pinnedUntilLabel } from "../utils/pin-range";
import { placeWindow } from "../utils/place-window";
import { eyebrowSx, fmtHours, fmtHoursShort, lbb } from "../utils/tokens";
import { MoveTarget } from "../utils/undo";
import { shiftLabelFor, WeekDayHeader, WeekLane, WeekLaneDay, WeekLanes } from "../utils/week-lanes"; // eslint-disable-line sort-imports
import { AvailabilityBadge, availabilityStripe, availabilityTitle, OUT_NAME_OPACITY, OutCount } from "./availability-badge"; // eslint-disable-line sort-imports
import { FillRequest } from "./fill-picker";
import { CardMenu, dropSurface, useCardDrag, useCardDrop, useCardHover, useLaneHeaderDrop, WorkerDragItem } from "./manual-controls"; // eslint-disable-line sort-imports
import { PeopleList, WORKER_LIST_WIDTH } from "./people-list";
import { KnownWorker } from "./standing-crew-panel";
import { StepDetails } from "./step-details";

/** yyyy-MM-dd a worker's standing pin on a work center ends, if one covers the given day (yyyy-MM-dd). */
export type PinEndOnDay = (workerId: string, workstation: string, day: string) => string | null;

const LANE_HEADER_WIDTH = 132;
/** A day column never squeezes below this; the lanes scroll sideways instead. */
const MIN_DAY_WIDTH = 190;
const GRID = `${LANE_HEADER_WIDTH}px repeat(7, minmax(${MIN_DAY_WIDTH}px, 1fr))`;
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const crewColor = (have: number, needed: number, out = 0): string =>
	needed === 0 ? lbb.ink60 : have === 0 || out > 0 ? lbb.brick : have < needed ? lbb.warning : lbb.success;

// ---------------------------------------------------------------------------

/** A drafted placement shown in its lane header until Allocate saves it. */
const DraftChip: React.FC<{ draft: DraftPin; onRemove: (workerId: string) => void }> = ({ draft, onRemove }) => {
	const remove = useCallback(() => onRemove(draft.workerId), [onRemove, draft.workerId]);
	return (
		<span
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 4,
				padding: "2px 4px 2px 8px",
				border: `1px dashed ${lbb.brick}`,
				borderRadius: 999,
				background: lbb.paper,
				color: lbb.brick,
				fontFamily: lbb.fontUi,
				fontSize: 11,
				fontWeight: 600,
				maxWidth: "100%",
				minWidth: 0,
			}}
			title={`${draft.workerName} drafted on this line for the week${draft.force ? " (moved from another line)" : ""}. Applied when you press Allocate.`}
		>
			<span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{draft.workerName}</span>
			<button
				type="button"
				onClick={remove}
				aria-label={`Drop the draft for ${draft.workerName}`}
				title="Drop this draft"
				style={{ border: 0, background: "transparent", color: lbb.brick, cursor: "pointer", fontSize: 13, lineHeight: 1, padding: "0 2px" }}
			>
				×
			</button>
		</span>
	);
};

// ---------------------------------------------------------------------------

/** One person on a phase that day: a draggable name chip with the card menu. */
/** Half-filled dot: the person covers only part of their crew line's hours (times on hover). */
const PartDot: React.FC<{ inverted: boolean }> = ({ inverted }) => {
	const color = inverted ? lbb.paper : lbb.navy;
	return (
		<span
			aria-hidden
			style={{
				flexShrink: 0,
				width: 7,
				height: 7,
				borderRadius: "50%",
				border: `1.5px solid ${color}`,
				background: `linear-gradient(90deg, ${color} 50%, transparent 50%)`,
			}}
		/>
	);
};

const Chip: React.FC<{
	card: LaneCard;
	stepTitle: string;
	phaseLabel: string;
	selected: boolean;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	pinnedUntil: string | null;
	/** The crew line's window in decimal hours; a card covering less of it gets a half-filled dot. */
	lineStart?: number;
	lineEnd?: number;
}> = ({ card, stepTitle, phaseLabel, selected, moves, onSelect, pinnedUntil, lineStart, lineEnd }) => {
	const partial =
		lineStart !== undefined && lineEnd !== undefined && (card.start > lineStart + 0.01 || card.end < lineEnd - 0.01);
	const label = `${phaseLabel}${card.segmentSeq != null ? ` · part ${card.segmentSeq}` : ""}`;
	const item = useMemo(
		() => ({
			id: card.id,
			label,
			workerId: card.workerId,
			workerName: card.workerName,
			workOrderId: card.workOrderId,
			phaseId: card.phaseId,
			dayIso: card.dayIso,
		}),
		[card.id, label, card.workerId, card.workerName, card.workOrderId, card.phaseId, card.dayIso]
	);
	const { isDragging, dragRef } = useCardDrag(item, moves);
	const pinText = card.isManuallyAssigned ? (pinnedUntil ? ` · ${pinnedUntilLabel(pinnedUntil)}` : " · pinned") : "";
	const title = `${card.workerName} · ${stepTitle} · ${label} · ${hhmm(card.start)} – ${hhmm(card.end)}${partial ? ` (line runs ${hhmm(lineStart ?? 0)} – ${hhmm(lineEnd ?? 0)})` : ""}${pinText}${availabilityTitle(card.unavailable)}`;
	const toggle = useCallback(() => onSelect(selected ? null : card.id), [onSelect, selected, card.id]);
	const handleClick = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			toggle();
		},
		[toggle]
	);
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				toggle();
			}
		},
		[toggle]
	);
	const handleUnpin = useCallback(() => void moves.unpin(card.id), [moves, card.id]);
	const handleKeepWeek = useCallback(() => void moves.extend(card.id, "week"), [moves, card.id]);
	const handleKeepUntil = useCallback(() => moves.openUntil(card.id), [moves, card.id]);
	const handleReplace = useCallback(() => moves.replace(card.id), [moves, card.id]);
	const handleRemove = useCallback(() => moves.openRemove(card.id), [moves, card.id]);
	const handleSplit = useCallback(() => void moves.openSplit(card.id), [moves, card.id]);
	const { hover, handlers } = useCardHover();
	return (
		<span
			ref={dragRef}
			role="button"
			tabIndex={0}
			aria-label={title}
			title={title}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			{...handlers}
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 2,
				maxWidth: "100%",
				padding: "1px 2px 1px 7px",
				borderRadius: 999,
				background: selected ? lbb.brick : card.isManuallyAssigned ? lbb.paper : lbb.blockFill,
				border: selected ? lbb.borderInk : `1px solid ${card.isManuallyAssigned ? lbb.navy : lbb.navy20}`,
				color: selected ? lbb.paper : lbb.navy,
				fontFamily: lbb.fontUi,
				fontSize: 12,
				fontWeight: 600,
				lineHeight: 1.25,
				cursor: "grab",
				opacity: isDragging ? 0.35 : 1,
				...availabilityStripe(card.unavailable),
			}}
		>
			{/* Names wrap onto a second line rather than ellipsising. */}
			<span style={{ minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere", opacity: card.isOut ? OUT_NAME_OPACITY : 1 }}>
				{card.workerName}
			</span>
			{partial && <PartDot inverted={selected} />}
			{card.unavailable && <AvailabilityBadge row={card.unavailable} inverted={selected} />}
			<CardMenu
				pinned={card.isManuallyAssigned}
				visible={hover}
				urgent={Boolean(card.unavailable)}
				onUnpin={handleUnpin}
				onKeepWeek={handleKeepWeek}
				onKeepUntil={handleKeepUntil}
				onReplace={handleReplace}
				onRemove={handleRemove}
				onSplit={handleSplit}
				pinnedUntil={pinnedUntil}
				color={selected ? lbb.paper : card.unavailable || card.isManuallyAssigned ? lbb.brick : lbb.ink60}
				size={12}
			/>
		</span>
	);
};

const EmptySlot: React.FC<{ jobRole: string; slot: LaneSlot; onAssign: (slot: LaneSlot) => void }> = ({ jobRole, slot, onAssign }) => {
	const handleClick = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			onAssign(slot);
		},
		[onAssign, slot]
	);
	return (
		<button
			type="button"
			onClick={handleClick}
			title={`${hhmm(slot.start)} – ${hhmm(slot.end)}`}
			style={{
				display: "block",
				width: "100%",
				boxSizing: "border-box",
				padding: "2px 7px",
				border: `1.5px dashed ${lbb.navy40}`,
				borderRadius: 999,
				background: "transparent",
				color: lbb.ink60,
				fontFamily: lbb.fontUi,
				fontSize: 11,
				fontWeight: 600,
				cursor: "pointer",
				textAlign: "left",
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
			}}
		>
			+ Assign {jobRole}
		</button>
	);
};

// ---------------------------------------------------------------------------

/** A crew group inside a day cell: the drop target, its chips under one label per shift, its open slots. */
const Group: React.FC<{
	step: LaneStep;
	group: LaneCrewGroup;
	dayIso: string;
	shifts: ShiftInstance[];
	selected: number | null;
	/** The day the click-selected chip sits in, so a same-day check works without a drag. */
	selectedDay: string | null;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	onFill: (request: FillRequest) => void;
	pinEndFor: (workerId: string, day: string) => string | null;
	workstation: string;
}> = ({ step, group, dayIso, shifts, selected, selectedDay, moves, onSelect, onFill, pinEndFor, workstation }) => {
	const droppable = group.dropPhaseId !== null;
	const target = useMemo<MoveTarget>(
		() => ({
			kind: "phase",
			workOrderId: step.workOrderId,
			phaseId: group.dropPhaseId ?? -1,
			label: `${step.title} · ${group.operationName}`,
		}),
		[step.workOrderId, step.title, group.dropPhaseId, group.operationName]
	);
	// The group reads as its most welcoming phase.
	const { targetIndex } = moves;
	const grade = useMemo(
		() => (droppable ? bestPhaseTarget(group.phaseIds.map((id) => targetIndex.phase(step.workOrderId, id))) : undefined),
		[droppable, group.phaseIds, targetIndex, step.workOrderId]
	);
	const targetsLoaded = targetIndex.loaded;
	// The picked-up card's day: a drag carries it; a click-selected chip's
	// day is looked up by the view.
	const cardDay = moves.dragging?.dayIso ?? (selected !== null ? selectedDay : null);
	const state = useMemo<DropState>(
		() =>
			droppable
				? dayPhaseDropState(cardDay, dayIso, grade, targetsLoaded)
				: { ...dayPhaseDropState(cardDay, dayIso, undefined), allowed: false },
		[droppable, cardDay, dayIso, grade, targetsLoaded]
	);
	// A name from the people list: one manual block on this step for this
	// day's shift (the open slot's window when there is one).
	const handlePlace = useCallback(
		(worker: WorkerDragItem) => {
			const window = placeWindow(dayIso, step, group);
			void moves.placeWorker({
				workerId: worker.workerId,
				workerName: worker.workerName,
				workOrderId: step.workOrderId,
				phaseId: window.phaseId,
				jobRoleId: group.jobRoleId,
				jobRoleName: group.jobRole,
				start: window.start,
				end: window.end,
				workstation,
				stepTitle: step.title,
			});
		},
		[moves, dayIso, step, group, workstation]
	);
	// Week-mode moves carry the whole step by default.
	const { isOver, canDrop, chipOver, dropRef } = useCardDrop(target, state, moves, true, handlePlace);
	const receiving = selected !== null && droppable;
	const handoff = useCallback(() => {
		if (selected !== null && droppable && state.allowed) {
			moves.requestMove(selected, target, {
				wholeStep: true,
				confirmFirst: state.confirm ? `${state.caption || "Not qualified"} for ${group.jobRole}. Place anyway?` : null,
			});
			onSelect(null);
		}
	}, [selected, droppable, moves, target, state.allowed, state.confirm, state.caption, group.jobRole, onSelect]);
	const onKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (receiving && (event.key === "Enter" || event.key === " ")) {
				event.preventDefault();
				handoff();
			}
		},
		[receiving, handoff]
	);
	const handleAssign = useCallback(
		(slot: LaneSlot) =>
			onFill({
				workOrderId: step.workOrderId,
				phaseId: slot.phaseId,
				start: slot.startIso,
				end: slot.endIso,
				jobRoleId: group.jobRoleId,
				jobRoleName: group.jobRole,
				title: `${step.title} · ${group.operationName} · ${group.jobRole}`,
			}),
		[onFill, step.workOrderId, step.title, group.jobRoleId, group.jobRole, group.operationName]
	);

	// Chips grouped by shift, in shift order of first appearance — one label
	// per distinct shift in the group, not per name or per phase.
	const byShift = useMemo(() => {
		const map = new Map<string, LaneCard[]>();
		group.cards.forEach((card) => {
			const key = shiftLabelFor(card.shiftId, card.start, shifts);
			map.set(key, [...(map.get(key) || []), card]);
		});
		return Array.from(map.entries());
	}, [group.cards, shifts]);
	const dragging = Boolean(moves.dragging);

	return (
		<div
			ref={dropRef}
			role={receiving ? "button" : undefined}
			tabIndex={receiving ? 0 : undefined}
			aria-label={receiving ? `Move the selected block to ${step.title} · ${group.operationName}` : undefined}
			onClick={handoff}
			onKeyDown={onKeyDown}
			style={{
				padding: "4px 6px 6px",
				borderTop: lbb.borderHair,
				borderRadius: 3,
				cursor: receiving ? "copy" : "default",
				background: receiving || chipOver ? lbb.dropTint : "transparent",
				...dropSurface(state, (dragging || receiving) && droppable, isOver, canDrop),
				...(chipOver ? { outline: `2px solid ${lbb.navy}`, outlineOffset: -2 } : {}),
			}}
		>
			<div style={{ display: "flex", alignItems: "baseline", gap: 6, paddingBottom: 3, minWidth: 0 }}>
				<span
					style={{ ...eyebrowSx, fontSize: 9, color: lbb.ink60, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
					title={group.jobRole ? `${group.operationName} · ${group.jobRole}` : group.operationName}
				>
					{group.operationName}
				</span>
				{group.needed > 0 && (
					<span
						style={{ fontFamily: lbb.fontMono, fontSize: 10, fontWeight: 700, color: crewColor(group.have, group.needed, group.outCount) }}
						title={`${group.have} of ${group.needed} ${group.jobRole || "crew"}${group.outCount > 0 ? ` · ${group.outCount} assigned but out` : ""}`}
					>
						{group.have}/{group.needed}
						<OutCount count={group.outCount} size={10} />
					</span>
				)}
				{(dragging || receiving) && droppable && state.caption && (
					<span style={{ fontFamily: lbb.fontUi, fontSize: 10, color: state.outline, marginLeft: "auto", whiteSpace: "nowrap" }}>
						{state.caption}
					</span>
				)}
			</div>
			<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
				{byShift.map(([shiftLabel, cards]) => (
					<div key={shiftLabel} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
						<span style={{ fontFamily: lbb.fontMono, fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase", color: lbb.navy40 }}>
							{shiftLabel}
						</span>
						<div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
							{cards.map((card) => (
								<Chip
									key={card.id}
									card={card}
									stepTitle={step.title}
									phaseLabel={group.operationName}
									selected={selected === card.id}
									moves={moves}
									onSelect={onSelect}
									pinnedUntil={pinEndFor(card.workerId, card.dayIso)}
									lineStart={group.start}
									lineEnd={group.end}
								/>
							))}
						</div>
					</div>
				))}
				{group.slots.map((slot, index) => (
					<EmptySlot key={`slot-${step.workOrderId}-${slot.phaseId}-${slot.startIso}-${index}`} jobRole={group.jobRole} slot={slot} onAssign={handleAssign} />
				))}
			</div>
		</div>
	);
};

// ---------------------------------------------------------------------------

const StepCell: React.FC<{
	step: LaneStep;
	day: WeekLaneDay;
	shifts: ShiftInstance[];
	selected: number | null;
	selectedDay: string | null;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	onFill: (request: FillRequest) => void;
	onOpenDay: (date: Date) => void;
	pinEndFor: (workerId: string, day: string) => string | null;
	workstation: string;
}> = ({ step, day, shifts, selected, selectedDay, moves, onSelect, onFill, onOpenDay, pinEndFor, workstation }) => {
	const groups = useMemo(() => groupCrewLines(step.phases), [step.phases]);
	const window = `${hhmm(step.start)} – ${hhmm(step.end)}`;
	const handleOpenDay = useCallback(() => onOpenDay(day.date), [onOpenDay, day.date]);
	return (
		<div style={{ border: `1px solid ${lbb.navy20}`, borderRadius: 4, background: lbb.paper, overflow: "hidden" }}>
			<StepDetails step={step} onOpenDay={handleOpenDay} openDayLabel={`Open ${format(day.date, "EEE d MMM")} by line`}>
			{(handleDetails) => (
			<button
				type="button"
				onClick={handleDetails}
				aria-label={`${step.title}, ${window}, ${step.crewHave} of ${step.crewNeeded} crew lines filled. Show details`}
				style={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
					width: "100%",
					padding: "5px 7px",
					background: lbb.alloyWarm,
					border: "none",
					cursor: "pointer",
					textAlign: "left",
					font: "inherit",
					minWidth: 0,
				}}
			>
				<span style={{ display: "flex", alignItems: "baseline", gap: 6, width: "100%", minWidth: 0 }}>
					<span style={{ fontFamily: lbb.fontUi, fontSize: 12, fontWeight: 700, color: lbb.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
						{step.title}
					</span>
					<span style={{ marginLeft: "auto", fontFamily: lbb.fontMono, fontSize: 11, fontWeight: 700, color: crewColor(step.crewHave, step.crewNeeded, step.crewOut), whiteSpace: "nowrap" }}>
						{step.crewHave}/{step.crewNeeded}
						<OutCount count={step.crewOut} size={11} />
					</span>
				</span>
			</button>
			)}
			</StepDetails>
			{step.phases.length === 0 && (
				<div style={{ padding: "4px 7px", fontFamily: lbb.fontUi, fontSize: 11, color: lbb.ink60 }}>No labor standard matched.</div>
			)}
			{groups.map((group) => (
				<Group
					key={group.key}
					step={step}
					group={group}
					dayIso={day.dayIso}
					shifts={shifts}
					selected={selected}
					selectedDay={selectedDay}
					moves={moves}
					onSelect={onSelect}
					onFill={onFill}
					pinEndFor={pinEndFor}
									workstation={workstation}
				/>
			))}
		</div>
	);
};

// ---------------------------------------------------------------------------

/** One work center's row: the header (drop target for a worker chip) and seven day cells. */
const Lane: React.FC<{
	lane: WeekLane;
	shifts: ShiftInstance[];
	selected: number | null;
	selectedDay: string | null;
	moves: ManualMoves;
	onSelect: (id: number | null) => void;
	onFill: (request: FillRequest) => void;
	onOpenDay: (date: Date) => void;
	pinEnd: PinEndOnDay;
}> = ({ lane, shifts, selected, selectedDay, moves, onSelect, onFill, onOpenDay, pinEnd }) => {
	const { isOver, dragging, dropRef } = useLaneHeaderDrop(lane.workcenter, moves);
	const pinEndFor = useCallback(
		(workerId: string, day: string) => pinEnd(workerId, lane.workcenter, day),
		[pinEnd, lane.workcenter]
	);
	const headerSurface: React.CSSProperties = dragging
		? { outline: `2px ${isOver ? "solid" : "dashed"} ${lbb.navy}`, outlineOffset: -2, background: isOver ? lbb.dropTint : "transparent" }
		: {};
	const drafts = moves.draftPins.filter((d) => d.workstation === lane.workcenter);
	const header = (
		<div
			ref={dropRef}
			style={{
				display: "flex",
				flexDirection: "column",
				gap: 2,
				padding: "8px 10px",
				borderRadius: 3,
				minWidth: 0,
				...headerSurface,
			}}
			title={`Drop a name here to keep them on ${lane.workcenter} for the week`}
		>
			<span style={{ fontFamily: lbb.fontUi, fontSize: lane.idle ? 12 : 14, fontWeight: 600, color: lane.idle ? lbb.ink60 : lbb.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
				{lane.workcenter}
			</span>
			<span style={{ fontFamily: lbb.fontMono, fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: lbb.ink60 }}>
				{lane.idle ? "idle this week" : `${fmtHoursShort(lane.weekHours)} this week`}
			</span>
			{drafts.length > 0 && (
				<div style={{ display: "flex", flexWrap: "wrap", gap: 4, paddingTop: 4 }}>
					{drafts.map((draft) => (
						<DraftChip key={draft.workerId} draft={draft} onRemove={moves.removeDraftPin} />
					))}
				</div>
			)}
		</div>
	);

	if (lane.idle) {
		return (
			<div style={{ display: "grid", gridTemplateColumns: GRID, alignItems: "center", padding: "0 16px", borderBottom: lbb.borderHair, gap: 6, background: lbb.alloy }}>
				{header}
				<div style={{ gridColumn: "2 / span 7", fontFamily: lbb.fontUi, fontSize: 11, color: lbb.navy40 }}>No steps planned on this line.</div>
			</div>
		);
	}

	return (
		<div style={{ display: "grid", gridTemplateColumns: GRID, alignItems: "start", padding: "6px 16px", borderBottom: lbb.borderHair, gap: 6 }}>
			{header}
			{lane.days.map((day) => (
				<div key={day.dayIso} style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
					{day.steps.map((step) => (
						<StepCell
							key={step.workOrderId}
							step={step}
							day={day}
							shifts={shifts}
							selected={selected}
							selectedDay={selectedDay}
							moves={moves}
							onSelect={onSelect}
							onFill={onFill}
							onOpenDay={onOpenDay}
							pinEndFor={pinEndFor}
							workstation={lane.workcenter}
						/>
					))}
					{day.loose.length > 0 && (
						<div style={{ display: "flex", flexWrap: "wrap", gap: 3, padding: "2px 0" }}>
							{day.loose.map((card) => (
								<Chip
									key={card.id}
									card={card}
									stepTitle="another week's step"
									phaseLabel={card.jobRole}
									selected={selected === card.id}
									moves={moves}
									onSelect={onSelect}
									pinnedUntil={pinEndFor(card.workerId, card.dayIso)}
								/>
							))}
						</div>
					)}
				</div>
			))}
		</div>
	);
};

// ---------------------------------------------------------------------------

const DayHeader: React.FC<{ day: WeekDayHeader; index: number }> = ({ day, index }) => (
	<div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
		<span style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
			<span style={{ fontFamily: lbb.fontMono, letterSpacing: "0.06em" }}>{DOW[index].toUpperCase()}</span>
			<span style={{ fontFamily: lbb.fontMono, fontWeight: 400, color: lbb.navy40 }}>{format(day.date, "dd")}</span>
		</span>
		<span
			style={{
				fontFamily: lbb.fontMono,
				fontSize: 11,
				fontWeight: 700,
				letterSpacing: 0,
				textTransform: "none",
				color: day.coveragePct === null ? lbb.navy40 : crewColor(day.have, day.needed, day.outCount),
			}}
			title={
				day.coveragePct === null
					? "nothing to crew"
					: `${day.have} of ${day.needed} crew lines filled${day.outCount > 0 ? ` · ${day.outCount} assigned but out` : ""}`
			}
		>
			{day.coveragePct === null ? "—" : `${day.coveragePct}% covered`}
			<OutCount count={day.outCount} size={11} />
		</span>
	</div>
);

export interface WeekLineViewProps {
	lanes: WeekLanes;
	/** Everyone with an assignment this week — the draggable names on the left edge. */
	workers: KnownWorker[];
	shifts: ShiftInstance[];
	selected: number | null;
	onSelect: (id: number | null) => void;
	moves: ManualMoves;
	onFill: (request: FillRequest) => void;
	/** A step header was clicked: open that day by line. */
	onOpenDay: (date: Date) => void;
	/** Standing-pin end date for a worker on a work center on a day (yyyy-MM-dd). */
	pinEnd: PinEndOnDay;
}

const WeekLineView: React.FC<WeekLineViewProps> = ({ lanes, workers, shifts, selected, onSelect, moves, onFill, onOpenDay, pinEnd }) => {
	const sortedWorkers = useMemo(() => [...workers].sort((a, b) => a.name.localeCompare(b.name)), [workers]);
	const draftedOn = useMemo(() => new Map(moves.draftPins.map((d) => [String(d.workerId), d.workstation])), [moves.draftPins]);
	// The day the click-selected chip sits in — a drop on another day is refused.
	const selectedDay = useMemo<string | null>(() => {
		if (selected === null) {
			return null;
		}
		for (const lane of lanes.lanes) {
			for (const day of lane.days) {
				const hit =
					day.steps.some((step) => step.phases.some((phase) => phase.cards.some((card) => card.id === selected))) ||
					day.loose.some((card) => card.id === selected);
				if (hit) {
					return day.dayIso;
				}
			}
		}
		return null;
	}, [selected, lanes]);
	const busy = lanes.lanes.filter((lane) => !lane.idle);
	const idle = lanes.lanes.filter((lane) => lane.idle);
	return (
		<div style={{ display: "grid", gridTemplateColumns: `${WORKER_LIST_WIDTH}px minmax(0, 1fr)`, alignItems: "start" }}>
			<PeopleList
				title="People"
				hint="Drop a name on a step to place them for that shift. Drop it on the line name to keep them there all week (a draft until you press Allocate)."
				workers={sortedWorkers}
				draftedOn={draftedOn}
				emptyText="Nobody is rostered this week."
			/>

			{/* Lanes — scroll sideways only when seven MIN_DAY_WIDTH columns no longer fit. */}
			<div style={{ minWidth: 0, overflowX: "auto" }}>
				<div style={{ display: "grid", gridTemplateColumns: GRID, alignItems: "end", padding: "10px 16px", borderBottom: lbb.borderHair, gap: 6, ...eyebrowSx, color: lbb.ink60 }}>
					<div>Line · week hours</div>
					{lanes.days.map((day, index) => (
						<DayHeader key={day.dayIso} day={day} index={index} />
					))}
				</div>

				{busy.length === 0 && (
					<div style={{ padding: 48, textAlign: "center", fontFamily: lbb.fontUi, color: lbb.ink60 }}>No steps are planned this week.</div>
				)}

				{busy.map((lane) => (
					<Lane
						key={lane.workcenter}
						lane={lane}
						shifts={shifts}
						selected={selected}
						selectedDay={selectedDay}
						moves={moves}
						onSelect={onSelect}
						onFill={onFill}
						onOpenDay={onOpenDay}
						pinEnd={pinEnd}
					/>
				))}
				{idle.map((lane) => (
					<Lane
						key={lane.workcenter}
						lane={lane}
						shifts={shifts}
						selected={selected}
						selectedDay={selectedDay}
						moves={moves}
						onSelect={onSelect}
						onFill={onFill}
						onOpenDay={onOpenDay}
						pinEnd={pinEnd}
					/>
				))}
			</div>
		</div>
	);
};

export default WeekLineView;
